import { useCallback, useEffect, useRef, useState } from 'react'
import { SWIPE } from '../lib/constants'
import { useLatest } from './useLatest'
import type { Item } from '../lib/supabase'

// Delete + Undo (ux/05): the delete goes out at once, Undo puts the same row
// back where it was. An add from quick add's "From before" offers the same
// toast, and its Undo takes the new row off again. Adding a recipe's or the
// plan's ingredients (the choices sheet) is one "bulk" undo that carries its
// own way back. One pending undo at a time; a newer one replaces it (the older
// action stands).
export type PendingUndo =
  | { kind: 'delete'; item: Item; index: number; seq: number }
  | { kind: 'add'; id: string; name: string; seq: number }
  | { kind: 'bulk'; text: string; revert: () => void; seq: number }

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never

export function useUndoDelete(
  items: Item[],
  remove: (id: string) => void,
  restore: (item: Item, index: number) => void,
) {
  const [pending, setPending] = useState<PendingUndo | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout>>()
  const seq = useRef(0)
  const itemsRef = useLatest(items)

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const dismiss = useCallback(() => {
    if (timer.current) clearTimeout(timer.current)
    setPending(null)
  }, [])

  const offer = useCallback((next: DistributiveOmit<PendingUndo, 'seq'>) => {
    if (timer.current) clearTimeout(timer.current)
    seq.current += 1
    setPending({ ...next, seq: seq.current } as PendingUndo)
    timer.current = setTimeout(() => setPending(null), SWIPE.undoMs)
  }, [])

  const deleteWithUndo = useCallback((id: string) => {
    const index = itemsRef.current.findIndex(i => i.id === id)
    if (index < 0) return
    const item = itemsRef.current[index]
    remove(id)
    offer({ kind: 'delete', item, index })
  }, [remove, itemsRef, offer])

  /** The row is already added; this only offers to take it off again. */
  const addedWithUndo = useCallback((id: string, name: string) => {
    offer({ kind: 'add', id, name })
  }, [offer])

  /** Several writes already made; `revert` puts the list back as it was. */
  const bulkWithUndo = useCallback((text: string, revert: () => void) => {
    offer({ kind: 'bulk', text, revert })
  }, [offer])

  const pendingRef = useLatest(pending)

  const undo = useCallback(() => {
    const p = pendingRef.current
    if (timer.current) clearTimeout(timer.current)
    setPending(null)
    if (p?.kind === 'delete') restore(p.item, p.index)
    else if (p?.kind === 'add') remove(p.id)
    else if (p?.kind === 'bulk') p.revert()
  }, [restore, remove, pendingRef])

  return { pending, deleteWithUndo, addedWithUndo, bulkWithUndo, undo, dismiss }
}
